import { useState } from "react";
import { Card, CardContent, cn, Skeleton } from "@tellescope/ui";
import { formatDate } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { FileText, CheckCircle2, Clock, Play, ChevronRight } from "lucide-react";
import type { Form, FormResponse } from "@tellescope/types-models";
import type { PinnedFormsListBlock as PinnedFormsListBlockType } from "../../../shared/schema";
import { useEnduserPinnedForms, PinnedFormDisplay } from "../../hooks/useEnduserPinnedForms";
import { usePinnedFormsFilter } from "../../hooks/useItemFilter";
import { PinnedFormDialog } from "./PinnedFormDialog";
import { NativePinnedFormDialog } from "./NativePinnedFormDialog";
import { invalidateFormResponseQueries } from "../enduserQueryClient";
import { MobileList } from "../MobileList";
import { useTellescopeMutation } from "@tellescope/tanstack-query";
import { useEnduserCurrentEnduser } from "../../store/hooks";
import { useIframeToken } from "../../hooks/useIframeToken";
import { useIsNewFormsExperienceEnabled } from "../../../shared/store/portalSchemaStore";

/** Extended type with id for MobileList compatibility */
interface PinnedFormDisplayWithId extends PinnedFormDisplay {
  id: string;
}

interface PinnedFormsListBlockProps {
  block: PinnedFormsListBlockType;
  /** Optional form group ID to filter forms. When provided, only shows forms with outstanding responses in this group. */
  formGroupId?: string;
  /** Optional form group instance to filter forms. Used with formGroupId for more specific filtering. */
  formGroupInstance?: string;
}

interface PinnedFormCardProps {
  formDisplay: PinnedFormDisplay;
  onClick: () => void;
}

function PinnedFormCard({ formDisplay, onClick }: PinnedFormCardProps) {
  const { t } = useTranslation("forms");
  const { form, hasOutstanding, latestSubmitted } = formDisplay;

  return (
    <Card className="overflow-hidden cursor-pointer hover:bg-accent/50 transition-colors" onClick={onClick}>
      <CardContent className="px-4">
        <div className="flex gap-4">
          {/* Form icon */}
          <div className="flex-shrink-0">
            <div
              className={cn(
                "w-14 h-14 rounded-lg flex flex-col items-center justify-center",
                hasOutstanding
                  ? "bg-amber-100 dark:bg-amber-900/30"
                  : latestSubmitted
                  ? "bg-green-100 dark:bg-green-900/30"
                  : "bg-blue-100 dark:bg-blue-900/30"
              )}
            >
              {hasOutstanding ? (
                <Clock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              ) : latestSubmitted ? (
                <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
              ) : (
                <Play className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              )}
            </div>
          </div>

          {/* Form details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-sm truncate">{form.displayTitle || form.title}</h4>
            </div>

            {hasOutstanding && (
              <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400 mt-1">
                <FileText className="h-3 w-3" />
                <span>{t("Continue where you left off")}</span>
              </div>
            )}

            {!hasOutstanding && latestSubmitted && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <CheckCircle2 className="h-3 w-3" />
                <span>
                  {t("Last submitted {{timestamp}}", { timestamp: formatDate(latestSubmitted.submittedAt!) })}
                </span>
              </div>
            )}

            {!hasOutstanding && !latestSubmitted && (
              <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 mt-1">
                <Play className="h-3 w-3" />
                <span>{t("Click to start")}</span>
              </div>
            )}
          </div>

          {/* Chevron indicator */}
          <div className="flex-shrink-0 self-center">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PinnedFormsListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="px-4 py-3">
            <div className="flex gap-4">
              <Skeleton className="w-14 h-14 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function PinnedFormsListBlock({ block, formGroupId, formGroupInstance }: PinnedFormsListBlockProps) {
  const { t } = useTranslation("forms");
  const { title, emptyMessage, maxItems = 4, itemFilter } = block.props;

  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [selectedExistingResponse, setSelectedExistingResponse] = useState<FormResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preparedAccessCode, setPreparedAccessCode] = useState<string | null>(null);
  const [prepareError, setPrepareError] = useState<string | null>(null);

  const currentEnduser = useEnduserCurrentEnduser();
  const isNewFormsExperience = useIsNewFormsExperienceEnabled();
  const selectedFormUsesNewExperience = isNewFormsExperience(selectedForm?.id);

  const prepareFormResponseMutation = useTellescopeMutation({
    model: "form_responses",
    method: "prepare_form_response",
  });

  // Only the iframe path needs a token; it is fetched explicitly on form click.
  const iframeTokenQuery = useIframeToken({ enabled: false });

  const { forms: rawForms, isLoading: formsLoading, error } = useEnduserPinnedForms();

  // Apply item filter if configured
  const { filteredItems: filteredForms, isLoading: filterLoading } = usePinnedFormsFilter({
    items: rawForms,
    itemFilter,
  });

  const isLoading = formsLoading || filterLoading;

  // Apply form group filtering if formGroupId is provided
  // Only show forms that have outstanding responses matching the group
  const groupFilteredForms = formGroupId
    ? filteredForms.filter((f) => {
        const response = f.outstandingResponse;
        if (!response) return false;
        if (response.groupId !== formGroupId) return false;
        if (formGroupInstance && response.groupInstance !== formGroupInstance) return false;
        return true;
      })
    : filteredForms;

  // Add id field for MobileList compatibility (using form.id)
  const forms: PinnedFormDisplayWithId[] = groupFilteredForms.map((f) => ({ ...f, id: f.form.id }));

  const handleFormClick = async (formDisplay: PinnedFormDisplay) => {
    const { form, outstandingResponse } = formDisplay;
    setSelectedForm(form);
    setSelectedExistingResponse(outstandingResponse);
    setPreparedAccessCode(null);
    setPrepareError(null);
    setDialogOpen(true);

    try {
      if (outstandingResponse?.accessCode) {
        setPreparedAccessCode(outstandingResponse.accessCode);
      } else {
        if (!currentEnduser) {
          throw new Error("User session not available");
        }

        const result = await prepareFormResponseMutation.mutateAsync({
          formId: form.id,
          enduserId: currentEnduser.id,
          title: form.displayTitle || form.title,
        });
        setPreparedAccessCode(result.response.accessCode ?? null);
      }

      if (!isNewFormsExperience(form.id)) {
        iframeTokenQuery.refetch();
      }
    } catch (err) {
      setPrepareError(err instanceof Error ? err.message : "Failed to load form");
    }
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Invalidate form response queries to refresh after potential submission
      invalidateFormResponseQueries();

      // Clear selection after dialog animation completes
      setTimeout(() => {
        setSelectedForm(null);
        setSelectedExistingResponse(null);
        setPreparedAccessCode(null);
        setPrepareError(null);
        prepareFormResponseMutation.reset();
      }, 200);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {title && <h3 className="text-lg font-semibold">{title}</h3>}
        <PinnedFormsListSkeleton count={maxItems} />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load forms")}</CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {title && <h3 className="text-lg font-semibold">{title}</h3>}
        <MobileList
          items={forms}
          renderItem={(formDisplay) => (
            <PinnedFormCard formDisplay={formDisplay} onClick={() => handleFormClick(formDisplay)} />
          )}
          title={title || t("Available Forms")}
          maxItems={maxItems}
          emptyMessage={emptyMessage || t("No forms available")}
        />
      </div>

      {selectedFormUsesNewExperience ? (
        <NativePinnedFormDialog
          form={selectedForm}
          existingResponse={selectedExistingResponse}
          isPreparingForm={prepareFormResponseMutation.isPending}
          error={prepareError}
          accessCode={preparedAccessCode}
          open={dialogOpen}
          onOpenChange={handleDialogClose}
        />
      ) : (
        <PinnedFormDialog
          form={selectedForm}
          existingResponse={selectedExistingResponse}
          accessCode={preparedAccessCode}
          iframeToken={iframeTokenQuery.token}
          isPreparingForm={prepareFormResponseMutation.isPending}
          isLoadingToken={iframeTokenQuery.isLoading}
          error={prepareError}
          open={dialogOpen}
          onOpenChange={handleDialogClose}
        />
      )}
    </>
  );
}
